import API_URLS from '../config/apiConfig';
import dayjs from 'dayjs';
import api from '../interceptors/axiosConfig';
import { formatActivityFeedTime } from '../utils/formatters/activityFeedFormatter';
import type { ActivityFeedApiResponse, ActivityFeedEvent, ActivityFeedItem, ActivityFeedResult } from '../types/activityFeed';
import type { ActivityFeedQuery } from '../types/services/activityFeed';

export type { ActivityFeedItem, ActivityFeedResult } from '../types/activityFeed';
export type { ActivityFeedQuery } from '../types/services/activityFeed';

const DEFAULT_LIMIT = 100;
const DEFAULT_OFFSET = 0;

const getActorName = (event: ActivityFeedEvent): string => {
  if (event.actor_name?.trim()) return event.actor_name.trim();
  if (event.actor_type === 'system') return 'System';
  return 'Unknown';
};

const getActorEmail = (event: ActivityFeedEvent): string => {
  if (event.actor_type === 'system') return 'system';
  return event.actor_name?.trim() || 'unknown';
};

const mapActivityFeedEvent = (event: ActivityFeedEvent): ActivityFeedItem => ({
  id: event.id,
  userName: getActorName(event),
  userEmail: getActorEmail(event),
  action: event.summary?.trim() || event.event_type_label_key?.trim() || event.event_type,
  time: formatActivityFeedTime(event.occurred_at),
  occurredAt: event.occurred_at,
  eventType: event.event_type,
  eventCategory: event.event_category || 'unknown',
  actorType: event.actor_type || 'unknown',
  source: event.source || 'unknown',
  summary: event.summary?.trim() || '',
  storeId: event.store_id,
  eventTypeLabelKey: event.event_type_label_key || '',
  actorUserId: event.actor_user_id,
  actorStoreUserId: event.actor_store_user_id,
  actorName: event.actor_name,
  entityTable: event.entity_table,
  entityId: event.entity_id,
  entityLabel: event.entity_label,
  detailsJson: event.details_json,
  beforeJson: event.before_json,
  afterJson: event.after_json,
  requestId: event.request_id,
  correlationId: event.correlation_id,
});

class ActivityFeedService {
  async getActivityFeed(storeId: string, query: ActivityFeedQuery = {}): Promise<ActivityFeedResult> {
    const limit = query.limit ?? DEFAULT_LIMIT;
    const offset = query.offset ?? DEFAULT_OFFSET;
    const startDate = query.startDate
      ? dayjs(query.startDate).startOf('day').format('YYYY-MM-DD HH:mm:ss')
      : undefined;
    const endDate = query.endDate
      ? dayjs(query.endDate).endOf('day').format('YYYY-MM-DD HH:mm:ss')
      : undefined;
    const eventType = query.eventType?.trim() || undefined;
    const eventCategory = query.eventCategory?.trim() || undefined;
    const response = await api.get<ActivityFeedApiResponse>(
      API_URLS.GET_STORE_EVENT_LOG(storeId, limit, offset, startDate, endDate, eventType, eventCategory),
    );
    const events = Array.isArray(response.data?.events) ? response.data.events : [];
    return {
      events: events.map(mapActivityFeedEvent),
      total: typeof response.data?.total === 'number' ? response.data.total : 0,
    };
  }
}

const activityFeedService = new ActivityFeedService();

export default activityFeedService;
